<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import DatePicker from 'vue-datepicker-next';
import ptBR from 'vue-datepicker-next/locale/pt-br.es';
import { calendarParts } from './calendarTime';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  value: { type: Date, default: null },
  disabled: { type: Boolean, default: false },
  label: { type: String, required: true },
});
const emit = defineEmits(['change']);
const root = ref(null);
const panel = ref(null);
const host = ref(null);
const isOpen = ref(false);
const position = ref({});
const dateLabel = computed(() =>
  props.value
    ? new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        ...(props.value.getFullYear() !== calendarParts(new Date()).year
          ? { year: 'numeric' }
          : {}),
      }).format(props.value)
    : props.label
);

const updatePosition = async () => {
  if (!isOpen.value) return;
  await nextTick();
  if (!root.value || !panel.value) return;
  const trigger = root.value.getBoundingClientRect();
  const padding = 8;
  const viewport = window.visualViewport;
  const width = viewport?.width || window.innerWidth;
  const height = viewport?.height || window.innerHeight;
  const originX = viewport?.offsetLeft || 0;
  const originY = viewport?.offsetTop || 0;
  const panelWidth = Math.min(280, width - padding * 2);
  const panelHeight = Math.min(panel.value.scrollHeight, height - padding * 2);
  const left = Math.max(
    originX + padding,
    Math.min(trigger.left, originX + width - panelWidth - padding)
  );
  const below = trigger.bottom + 6;
  const top = Math.max(
    originY + padding,
    Math.min(
      below + panelHeight <= originY + height - padding
        ? below
        : trigger.top - panelHeight - 6,
      originY + height - panelHeight - padding
    )
  );
  position.value = {
    left: `${left}px`,
    top: `${top}px`,
    width: `${panelWidth}px`,
    maxHeight: `${height - padding * 2}px`,
  };
  await nextTick();
  if (!panel.value) return;
  const actual = panel.value.getBoundingClientRect();
  position.value = {
    ...position.value,
    left: `${left - (actual.left - left)}px`,
    top: `${top - (actual.top - top)}px`,
  };
};
const close = () => {
  isOpen.value = false;
};
const toggle = () => {
  if (props.disabled) return;
  if (isOpen.value) {
    close();
    return;
  }
  host.value = root.value?.closest('dialog') || document.body;
  isOpen.value = true;
  updatePosition();
};
const apply = value => {
  if (
    props.disabled ||
    !(value instanceof Date) ||
    Number.isNaN(value.getTime())
  )
    return;
  emit('change', value);
  close();
};
const onOutside = event => {
  if (
    isOpen.value &&
    !root.value?.contains(event.target) &&
    !panel.value?.contains(event.target)
  )
    close();
};
const onKeydown = event => {
  if (isOpen.value && event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    close();
    root.value?.querySelector('button')?.focus();
  }
};
onMounted(() => {
  document.addEventListener('pointerdown', onOutside);
  document.addEventListener('keydown', onKeydown, true);
  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition, true);
  window.visualViewport?.addEventListener('resize', updatePosition);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onOutside);
  document.removeEventListener('keydown', onKeydown, true);
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', updatePosition, true);
  window.visualViewport?.removeEventListener('resize', updatePosition);
});
</script>

<template>
  <div ref="root" class="min-w-0">
    <button
      type="button"
      class="flex h-10 w-full items-center gap-2 rounded-lg bg-n-alpha-black2 px-3 text-left text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus-visible:outline-n-brand disabled:opacity-50"
      :aria-label="`${label}: ${dateLabel}`"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="min-w-0 flex-1 truncate">{{ dateLabel }}</span>
      <span
        class="i-lucide-calendar-days size-4 shrink-0 text-n-slate-10"
        aria-hidden="true"
      />
    </button>
    <Teleport v-if="isOpen" :to="host">
      <div
        ref="panel"
        class="calendar-date-layer fixed z-[10000] overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg"
        :style="position"
        data-dialog-floating-layer="true"
        role="dialog"
        :aria-label="label"
        @click.stop
      >
        <DatePicker
          :value="value"
          type="date"
          open
          confirm
          confirm-text="Aplicar"
          :lang="ptBR"
          :clearable="false"
          :editable="false"
          :append-to-body="false"
          :popup-style="{
            position: 'static',
            top: 'auto',
            left: 'auto',
            margin: 0,
            border: 0,
            boxShadow: 'none',
          }"
          :input-attr="{ 'aria-label': label, tabindex: -1 }"
          @change="apply"
        />
        <Button
          type="button"
          variant="ghost"
          color="slate"
          label="Fechar"
          size="sm"
          class="mt-1 !w-full"
          @click="close"
        />
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.calendar-date-layer :deep(.mx-input-wrapper) {
  display: none;
}
.calendar-date-layer :deep(.mx-datepicker),
.calendar-date-layer :deep(.mx-datepicker-main),
.calendar-date-layer :deep(.mx-calendar) {
  width: 100%;
}
</style>
